/**
 * Crew-focused first-use hint overlays
 * Dismissible guidance popups (e.g. "Monitor during cruise for accurate cosmic dose")
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Modal,
  Platform,
} from 'react-native';
import { storage } from '../utils/storage';

const HINT_KEYS = {
  cruise: 'fvrad_hint_cruise_dismissed',
  liveMonitoring: 'fvrad_hint_live_monitoring_dismissed',
  highLatitude: 'fvrad_hint_high_latitude_dismissed',
} as const;

export interface CrewHint {
  id: keyof typeof HINT_KEYS;
  title: string;
  message: string;
}

const HINTS: CrewHint[] = [
  {
    id: 'cruise',
    title: 'Crew tip',
    message:
      'Monitor during cruise for accurate cosmic dose. Altitude and latitude affect exposure.',
  },
  {
    id: 'liveMonitoring',
    title: 'Live monitoring',
    message:
      'Enable live monitoring in-flight and cover the lens for real-time dose rate from camera sensor.',
  },
  {
    id: 'highLatitude',
    title: 'High-latitude routes',
    message:
      'Polar routes (e.g. JFK–LHR) have elevated cosmic radiation. Track carefully on transatlantic flights.',
  },
];

interface CrewHintOverlayProps {
  /** Which hint to show. If null, shows first non-dismissed hint on mount. */
  hintId?: keyof typeof HINT_KEYS | null;
  /** When set, show this hint (if not already dismissed). Cleared after show. */
  triggerHint?: keyof typeof HINT_KEYS | null;
  /** Callback when overlay is dismissed */
  onDismiss?: () => void;
}

export function CrewHintOverlay({
  hintId,
  triggerHint,
  onDismiss,
}: CrewHintOverlayProps) {
  const [visible, setVisible] = useState(false);
  const [currentHint, setCurrentHint] = useState<CrewHint | null>(null);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkHint(targetId: keyof typeof HINT_KEYS) {
      const hint = HINTS.find((h) => h.id === targetId);
      if (!hint) return;

      const key = HINT_KEYS[hint.id];
      const dismissed = await storage.getItem(key);

      if (!cancelled && !dismissed) {
        setCurrentHint(hint);
        setVisible(true);
      }
    }

    if (triggerHint) {
      checkHint(triggerHint);
    } else if (!initialCheckDone) {
      checkHint(hintId ?? 'cruise');
      setInitialCheckDone(true);
    }
    return () => {
      cancelled = true;
    };
  }, [hintId, triggerHint, initialCheckDone]);

  const handleDismiss = async () => {
    if (currentHint) {
      await storage.setItem(HINT_KEYS[currentHint.id], '1');
    }
    setVisible(false);
    setCurrentHint(null);
    onDismiss?.();
  };

  if (!visible || !currentHint) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleDismiss}
    >
      <Pressable style={styles.backdrop} onPress={handleDismiss}>
        <Pressable
          style={styles.card}
          onPress={(e) => e.stopPropagation()}
          accessibilityRole="alert"
        >
          <Text style={styles.title}>{currentHint.title}</Text>
          <Text style={styles.message}>{currentHint.message}</Text>
          <Pressable
            style={styles.dismissButton}
            onPress={handleDismiss}
            accessibilityLabel="Dismiss"
          >
            <Text style={styles.dismissText}>Got it</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** Programmatically show a specific hint (e.g. when user enables live monitoring) */
export async function showCrewHint(hintId: keyof typeof HINT_KEYS) {
  await storage.setItem(`fvrad_hint_${hintId}_pending`, '1');
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    maxWidth: 340,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 12,
      elevation: 8,
    }),
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: 8,
  },
  message: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
    marginBottom: 16,
  },
  dismissButton: {
    backgroundColor: '#00b8d9',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  dismissText: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: 15,
  },
});
